/**
 * Sanitizes HTML for lesson text blocks (paste + save).
 * Keeps bold/italic/underline, alignment, RTL, lists, tables,
 * links, text color, and highlighting. Strips scripts, images, font-family, and unsafe attrs.
 */

import { getTextBlockAlignmentStyle, isListMarkerOnlyText, LIST_MARKER_LEADING, type TextBlockAlign } from './richTextParagraphs';

export type { TextBlockAlign };
export { getTextBlockAlignmentStyle, isListMarkerOnlyText };

const BLOCK_TAGS = new Set([
  'P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'BLOCKQUOTE', 'PRE', 'TD', 'TH', 'TR', 'TABLE', 'UL', 'OL'
]);

const TABLE_SECTION_TAGS = new Set(['TABLE', 'TBODY', 'THEAD', 'TFOOT', 'TR']);

const INLINE_FORMAT_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U']);

const DANGEROUS_TAGS = new Set([
  'SCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'BUTTON',
  'TEXTAREA', 'SELECT', 'LINK', 'STYLE', 'META', 'BASE', 'SVG', 'MATH'
]);

type ParagraphAlign = {
  align: TextBlockAlign;
  rtl: boolean;
};

const isAllowedHref = (href: string): boolean => {
  const h = href.trim().toLowerCase();
  if (!h || h.startsWith('javascript:') || h.startsWith('data:') || h.startsWith('vbscript:')) {
    return false;
  }
  if (h.startsWith('http://') || h.startsWith('https://') || h.startsWith('mailto:')) return true;
  if (h.startsWith('#')) return true;
  if (h.startsWith('/') && !h.startsWith('//')) return true;
  return false;
};

const MSO_HIGHLIGHT_COLORS: Record<string, string> = {
  yellow: '#ffff00',
  green: '#00ff00',
  cyan: '#00ffff',
  turquoise: '#00ffff',
  magenta: '#ff00ff',
  blue: '#0000ff',
  red: '#ff0000',
  darkblue: '#000080',
  darkcyan: '#008080',
  darkgreen: '#008000',
  darkmagenta: '#800080',
  darkred: '#800000',
  darkyellow: '#808000',
  lightgray: '#d3d3d3',
  lightgrey: '#d3d3d3',
  darkgray: '#a9a9a9',
  darkgrey: '#a9a9a9',
  black: '#000000',
  white: '#ffffff',
};

const isSafeCssColor = (value: string | null | undefined): boolean => {
  if (!value?.trim()) return false;
  const v = value.trim().toLowerCase();
  if (v.includes('javascript') || v.includes('expression') || v.includes('url(')) return false;
  return true;
};

const normalizeColorValue = (raw: string | null | undefined): string | null => {
  if (!raw?.trim()) return null;
  const v = raw.trim();
  const named = MSO_HIGHLIGHT_COLORS[v.toLowerCase().replace(/\s+/g, '')];
  const candidate = named || v;
  return isSafeCssColor(candidate) ? candidate : null;
};

const isBlockMediaElement = (el: Element): boolean =>
  !!el.querySelector('br, table, ul, ol');

const visibleText = (text: string | null | undefined): string =>
  (text ?? '').replace(/\u00a0/g, ' ').replace(/\u200B/g, '').trim();

const isBlankLineElement = (el: HTMLElement): boolean => {
  if (el.querySelector('table, ul, ol, img')) return false;
  const text = visibleText(el.textContent);
  if (!text) return true;
  return isListMarkerOnlyText(el.textContent);
};

const hasMeaningfulBlockContent = (el: HTMLElement): boolean => {
  const text = visibleText(el.textContent);
  if (text && !isListMarkerOnlyText(el.textContent)) return true;
  return isBlockMediaElement(el);
};

/** Normalize a blank paragraph to a single line break — no stray list-marker text. */
const ensureBlankLineDiv = (doc: Document, div: HTMLDivElement): HTMLDivElement => {
  div.textContent = '';
  div.removeAttribute('data-list-item');
  div.removeAttribute('data-list-level');
  div.appendChild(doc.createElement('br'));
  return div;
};

/** Strip Office conditional comments and other non-content markup from raw HTML. */
const preprocessClipboardHtml = (html: string): string => {
  return html
    // Word hides <img> in non-VML conditional branches — keep those before stripping VML blocks.
    .replace(/<!--\[if\s+!vml\]>-->([\s\S]*?)<!--\[endif\]-->/gi, '$1')
    .replace(/<!--\[if\s+!mso\]>-->([\s\S]*?)<!--\[endif\]-->/gi, '$1')
    // Unwrap list-marker spans before other conditional comments are removed.
    .replace(/<!--\[if\s+!supportLists\]>-->([\s\S]*?)<!--\[endif\]-->/gi, '$1')
    .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '')
    .replace(/<!--StartFragment-->|<!--EndFragment-->/gi, '')
    .replace(/<(\/?)(?:o|w|v|m|st\d):[^>]*>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<meta[^>]*>/gi, '')
    .replace(/<link[^>]*>/gi, '')
    .replace(/<xml[\s\S]*?<\/xml>/gi, '');
};

const extractDocumentDirection = (html: string): 'rtl' | 'ltr' | null => {
  const bodyMatch = html.match(/<body[^>]*\sdir\s*=\s*["']?(rtl|ltr)["']?/i);
  if (bodyMatch) return bodyMatch[1].toLowerCase() as 'rtl' | 'ltr';
  const htmlMatch = html.match(/<html[^>]*\sdir\s*=\s*["']?(rtl|ltr)["']?/i);
  if (htmlMatch) return htmlMatch[1].toLowerCase() as 'rtl' | 'ltr';
  if (/<(?:html|body)[^>]*\slang\s*=\s*["']?(fa|ar|he|ur|ku|ps)/i.test(html)) {
    return 'rtl';
  }
  return null;
};

const RTL_LANG_CODES = new Set(['fa', 'ar', 'he', 'ur', 'ku', 'ps']);

/** Read alignment already stored on paragraph divs (survives save/re-sanitize). */
const readStoredParagraphAlign = (el: HTMLElement): ParagraphAlign | null => {
  const align = el.getAttribute('data-align') as TextBlockAlign | null;
  if (!align) return null;
  const rtl =
    el.getAttribute('data-direction') === 'rtl' ||
    align === 'justify-right' ||
    parseStyleMap(el.getAttribute('style') || '').get('direction')?.toLowerCase() === 'rtl';
  return { align, rtl };
};

const resolveParagraphAlign = (
  el: HTMLElement,
  elementDir: 'rtl' | 'ltr' | null,
  inheritedDir: 'rtl' | 'ltr' | null
): ParagraphAlign | null => {
  return (
    detectAlignment(
      el.getAttribute('style') || '',
      el.getAttribute('dir'),
      elementDir ?? inheritedDir,
      el.getAttribute('align')
    ) ?? readStoredParagraphAlign(el)
  );
};

const parseStyleMap = (style: string): Map<string, string> => {
  const map = new Map<string, string>();
  for (const part of style.split(';')) {
    const idx = part.indexOf(':');
    if (idx === -1) continue;
    const key = part.slice(0, idx).trim().toLowerCase();
    const value = part.slice(idx + 1).trim();
    if (key && value) {
      if (key.startsWith('mso-')) {
        if (key === 'mso-highlight') {
          const mapped = MSO_HIGHLIGHT_COLORS[value.toLowerCase().replace(/\s+/g, '')];
          if (mapped) map.set('background-color', mapped);
        }
        if (key === 'mso-bidi-language') {
          const v = value.toUpperCase();
          if (/^(AR|FA|HE|UR|KU|PS)/.test(v)) {
            map.set('direction', 'rtl');
          }
        }
        if (key === 'mso-table-dir') {
          const v = value.toLowerCase();
          if (v.includes('bidi') || v.includes('rtl')) {
            map.set('direction', 'rtl');
          }
        }
        continue;
      }
      map.set(key, value);
    }
  }
  return map;
};

const resolveDirection = (style: string, dir: string | null, inheritedDir: 'rtl' | 'ltr' | null): boolean => {
  const map = parseStyleMap(style);
  const explicit = map.get('direction')?.toLowerCase() || dir?.toLowerCase() || '';
  if (explicit === 'rtl') return true;
  if (explicit === 'ltr') return false;
  return inheritedDir === 'rtl';
};

const detectAlignment = (
  style: string,
  dir: string | null,
  inheritedDir: 'rtl' | 'ltr' | null,
  alignAttr?: string | null
): ParagraphAlign | null => {
  const map = parseStyleMap(style);
  const textAlign = map.get('text-align')?.toLowerCase() || alignAttr?.toLowerCase() || '';
  const paragraphDir = (map.get('direction')?.toLowerCase() || dir?.toLowerCase() || '') as
    | 'rtl'
    | 'ltr'
    | '';
  const rtlForJustify = resolveDirection(style, dir, inheritedDir);

  if (textAlign === 'right') {
    return { align: 'right', rtl: paragraphDir === 'rtl' || rtlForJustify };
  }
  if (textAlign === 'end') {
    return { align: 'right', rtl: paragraphDir === 'rtl' || rtlForJustify };
  }
  if (textAlign === 'left') {
    return { align: 'left', rtl: paragraphDir === 'rtl' };
  }
  if (textAlign === 'start') {
    if (paragraphDir === 'rtl') {
      return { align: 'right', rtl: true };
    }
    if (paragraphDir === 'ltr') {
      return { align: 'left', rtl: false };
    }
    return null;
  }
  if (textAlign === 'justify') {
    return { align: rtlForJustify ? 'justify-right' : 'justify', rtl: rtlForJustify };
  }
  if (textAlign === 'center') {
    return { align: 'left', rtl: false };
  }

  // Only explicit paragraph direction — do not infer alignment from document-level RTL.
  if (paragraphDir === 'rtl') {
    return { align: 'right', rtl: true };
  }
  if (paragraphDir === 'ltr') {
    return { align: 'left', rtl: false };
  }

  return null;
};

const detectCellAlignmentStyle = (style: string, dir: string | null): string | null => {
  const map = parseStyleMap(style);
  const parts: string[] = [];
  const textAlign = map.get('text-align');
  const direction = map.get('direction') || dir?.toLowerCase();
  const verticalAlign = map.get('vertical-align');

  if (textAlign && textAlign !== 'center') {
    parts.push(`text-align: ${textAlign};`);
  }
  if (direction === 'rtl' || direction === 'ltr') {
    parts.push(`direction: ${direction};`);
  }
  if (verticalAlign) {
    parts.push(`vertical-align: ${verticalAlign};`);
  }

  return parts.length ? parts.join(' ') : null;
};

const applyTableDirection = (
  table: HTMLTableElement,
  source: HTMLElement,
  inheritedDir: 'rtl' | 'ltr' | null
): void => {
  const storedDir = source.getAttribute('dir')?.toLowerCase();
  if (storedDir === 'rtl' || storedDir === 'ltr') {
    table.setAttribute('dir', storedDir);
    return;
  }
  const styleDir = parseStyleMap(source.getAttribute('style') || '').get('direction')?.toLowerCase();
  const resolved = styleDir === 'rtl' || styleDir === 'ltr'
    ? styleDir
    : getInheritedDirFromElement(source, inheritedDir);
  if (resolved === 'rtl' || resolved === 'ltr') {
    table.setAttribute('dir', resolved);
  }
};

const applyParagraphAttrs = (el: HTMLElement, paragraph: ParagraphAlign | null): void => {
  if (!paragraph) return;
  el.setAttribute('data-align', paragraph.align);
  if (paragraph.rtl && paragraph.align !== 'justify-right') {
    el.setAttribute('data-direction', 'rtl');
  } else {
    el.removeAttribute('data-direction');
  }
  el.setAttribute('style', getTextBlockAlignmentStyle(paragraph.align, { rtl: paragraph.rtl }));
};

const applyListDirectionFromBlock = (list: HTMLElement, block: HTMLElement): void => {
  const align = block.getAttribute('data-align');
  const direction = block.getAttribute('data-direction');
  if (direction === 'rtl' || align === 'right' || align === 'justify-right') {
    list.setAttribute('dir', 'rtl');
  } else if (align === 'left') {
    list.setAttribute('dir', direction === 'rtl' ? 'rtl' : 'ltr');
  }
};

const isOutputBlankDiv = (node: Node): boolean => {
  if (node.nodeType !== Node.ELEMENT_NODE || (node as Element).tagName !== 'DIV') {
    return false;
  }
  const div = node as HTMLDivElement;
  if (div.querySelector('table, ul, ol, img')) return false;
  const text = visibleText(div.textContent);
  if (text && !isListMarkerOnlyText(div.textContent)) return false;
  return true;
};

/** Apply RTL/right alignment to divs that follow an RTL paragraph but lack stored attrs (Word last line). */
const propagateRtlParagraphContext = (
  output: HTMLDivElement,
  documentDir: 'rtl' | 'ltr' | null
): void => {
  let context: ParagraphAlign | null =
    documentDir === 'rtl' ? { align: 'right', rtl: true } : null;

  for (const child of Array.from(output.children)) {
    if (child.nodeType !== Node.ELEMENT_NODE || (child as Element).tagName !== 'DIV') {
      continue;
    }
    const div = child as HTMLDivElement;
    if (div.querySelector('table, ul, ol')) {
      context = readStoredParagraphAlign(div);
      continue;
    }

    const stored = readStoredParagraphAlign(div);
    if (stored) {
      context = stored;
      continue;
    }

    if (context?.rtl && hasMeaningfulBlockContent(div)) {
      applyParagraphAttrs(div, {
        align: context.align === 'justify-right' ? 'justify-right' : 'right',
        rtl: true
      });
    }
  }
};

/** Word often emits several empty paragraphs for one visual gap — keep at most one. */
const collapseConsecutiveBlankDivs = (output: HTMLDivElement, doc: Document): void => {
  const nodes = Array.from(output.childNodes);
  output.innerHTML = '';

  let pendingBlank: HTMLDivElement | null = null;
  let lastParagraphRtl: ParagraphAlign | null = null;

  const flushBlank = () => {
    if (pendingBlank) {
      output.appendChild(pendingBlank);
      pendingBlank = null;
    }
  };

  for (const node of nodes) {
    if (isOutputBlankDiv(node)) {
      if (!pendingBlank) {
        pendingBlank = doc.createElement('div');
        pendingBlank.appendChild(doc.createElement('br'));
        if (lastParagraphRtl?.rtl) {
          applyParagraphAttrs(pendingBlank, {
            align: lastParagraphRtl.align === 'justify-right' ? 'justify-right' : 'right',
            rtl: true
          });
        }
      }
      continue;
    }
    flushBlank();
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'DIV') {
      const stored = readStoredParagraphAlign(node as HTMLDivElement);
      if (stored) {
        lastParagraphRtl = stored;
      }
    }
    output.appendChild(node);
  }
  flushBlank();
};

const isZeroIndent = (value: string): boolean =>
  /^0(?:cm|pt|px|em|rem|in|mm|%)?$/i.test(value.trim());

const extractBlockIndentStyle = (style: string, rtl: boolean): string | null => {
  const map = parseStyleMap(style);
  const parts: string[] = [];

  const marginLeft = map.get('margin-left');
  const marginRight = map.get('margin-right');
  const marginInlineStart = map.get('margin-inline-start');
  if (marginInlineStart && !isZeroIndent(marginInlineStart)) {
    parts.push(`margin-inline-start: ${marginInlineStart}`);
  } else {
    const startMargin = rtl ? marginRight || marginLeft : marginLeft || marginRight;
    if (startMargin && !isZeroIndent(startMargin)) {
      parts.push(`margin-inline-start: ${startMargin}`);
    }
  }

  const textIndent = map.get('text-indent');
  if (textIndent && !isZeroIndent(textIndent)) {
    parts.push(`text-indent: ${textIndent}`);
  }

  const paddingInlineStart = map.get('padding-inline-start');
  if (paddingInlineStart && !isZeroIndent(paddingInlineStart)) {
    parts.push(`padding-inline-start: ${paddingInlineStart}`);
  } else {
    const paddingLeft = map.get('padding-left');
    const paddingRight = map.get('padding-right');
    const startPad = rtl ? paddingRight || paddingLeft : paddingLeft || paddingRight;
    if (startPad && !isZeroIndent(startPad)) {
      parts.push(`padding-inline-start: ${startPad}`);
    }
  }

  return parts.length ? parts.join('; ') : null;
};

const mergeBlockStyle = (el: HTMLElement, extra: string | null): void => {
  if (!extra) return;
  const current = el.getAttribute('style') || '';
  const merged = current ? `${current.replace(/;\s*$/, '')}; ${extra}` : extra;
  el.setAttribute('style', merged);
};

const isWordListParagraph = (el: HTMLElement): boolean => {
  const cls = el.getAttribute('class')?.toLowerCase() || '';
  const style = el.getAttribute('style') || '';
  return cls.includes('msolistparagraph') || /mso-list\s*:/i.test(style);
};

const extractWordListLevel = (style: string): number => {
  const match = style.match(/level(\d+)/i);
  return match?.[1] ? Math.max(1, parseInt(match[1], 10)) : 1;
};

const detectWordListItemType = (el: HTMLElement): 'bullet' | 'number' | null => {
  const text = el.textContent?.replace(/\u00a0/g, ' ') || '';
  if (/^\s*\d+[.)]\s+\S/u.test(text) && isWordListParagraph(el)) return 'number';
  if (isWordListParagraph(el)) return 'bullet';
  return null;
};

const stripLeadingListMarkerFromBlock = (block: HTMLElement): void => {
  while (block.firstChild) {
    const node = block.firstChild;
    if (node.nodeType === Node.TEXT_NODE) {
      const original = node.textContent ?? '';
      const stripped = original.replace(LIST_MARKER_LEADING, '');
      if (!stripped && original.trim()) {
        block.removeChild(node);
        continue;
      }
      if (stripped !== original) {
        node.textContent = stripped;
      }
      break;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const child = node as HTMLElement;
      const tag = child.tagName;
      if (tag === 'SPAN' || tag === 'B' || tag === 'STRONG' || tag === 'I' || tag === 'EM' || tag === 'U') {
        const childText = child.textContent ?? '';
        const childStyle = child.getAttribute('style') || '';
        const isSymbolBullet =
          /symbol|wingdings|webdings/i.test(childStyle) ||
          isListMarkerOnlyText(childText);
        if (isSymbolBullet) {
          block.removeChild(node);
          continue;
        }
      }
      break;
    }
    break;
  }
};

const applyWordBlockLayout = (
  block: HTMLElement,
  source: HTMLElement,
  paragraph: ParagraphAlign | null,
  elementDir: 'rtl' | 'ltr' | null
): void => {
  const rtl =
    paragraph?.rtl ??
    (elementDir === 'rtl' || block.getAttribute('data-direction') === 'rtl');

  const listType = detectWordListItemType(source);
  if (listType) {
    if (!block.getAttribute('data-align')) {
      const listPara = resolveParagraphAlign(source, elementDir, null);
      applyParagraphAttrs(block, listPara);
    }
    block.setAttribute('data-list-item', listType);
    const level = extractWordListLevel(source.getAttribute('style') || '');
    if (level > 1) block.setAttribute('data-list-level', String(level));
    stripLeadingListMarkerFromBlock(block);
    return;
  }

  mergeBlockStyle(block, extractBlockIndentStyle(source.getAttribute('style') || '', rtl));
};

/** Turn consecutive Word list divs into semantic ul/ol with nesting. */
const groupConsecutiveListDivs = (output: HTMLDivElement, doc: Document): void => {
  const nodes = Array.from(output.childNodes);
  output.innerHTML = '';

  type ListStackEntry = { list: HTMLUListElement | HTMLOListElement; level: number };
  let stack: ListStackEntry[] = [];

  const flushStack = () => {
    stack = [];
  };

  const ensureListLevel = (
    level: number,
    listType: 'ul' | 'ol',
    reference: HTMLDivElement
  ): HTMLUListElement | HTMLOListElement => {
    if (stack.length === 0) {
      const list = doc.createElement(listType);
      applyListDirectionFromBlock(list, reference);
      output.appendChild(list);
      stack.push({ list, level: 1 });
    }

    while (stack.length < level) {
      const parentEntry = stack[stack.length - 1];
      if (!parentEntry) break;
      const parent = parentEntry.list;
      let lastLi = parent.lastElementChild as HTMLLIElement | null;
      if (!lastLi || lastLi.tagName !== 'LI') {
        lastLi = doc.createElement('li');
        parent.appendChild(lastLi);
      }
      const nested = doc.createElement(listType);
      applyListDirectionFromBlock(nested, reference);
      lastLi.appendChild(nested);
      stack.push({ list: nested, level: stack.length + 1 });
    }

    while (stack.length > level) {
      stack.pop();
    }

    const top = stack[stack.length - 1];
    if (!top) {
      const list = doc.createElement(listType);
      applyListDirectionFromBlock(list, reference);
      output.appendChild(list);
      stack.push({ list, level: 1 });
      return list;
    }
    if (top.list.tagName.toLowerCase() !== listType) {
      flushStack();
      const list = doc.createElement(listType);
      applyListDirectionFromBlock(list, reference);
      output.appendChild(list);
      stack.push({ list, level: 1 });
      return list;
    }

    return top.list;
  };

  for (const node of nodes) {
    if (node.nodeType !== Node.ELEMENT_NODE || (node as Element).tagName !== 'DIV') {
      flushStack();
      output.appendChild(node);
      continue;
    }

    const div = node as HTMLDivElement;
    const itemType = div.getAttribute('data-list-item');
    if (!itemType) {
      flushStack();
      output.appendChild(node);
      continue;
    }

    stripLeadingListMarkerFromBlock(div);
    const itemText = visibleText(div.textContent);
    if (!itemText || isListMarkerOnlyText(div.textContent)) {
      flushStack();
      continue;
    }

    const level = parseInt(div.getAttribute('data-list-level') || '1', 10);
    const listType = itemType === 'number' ? 'ol' : 'ul';
    const list = ensureListLevel(level, listType, div);

    const li = doc.createElement('li');
    const align = div.getAttribute('data-align') as TextBlockAlign | null;
    const direction = div.getAttribute('data-direction');
    if (align) {
      li.setAttribute('data-align', align);
      const rtl = direction === 'rtl';
      if (rtl && align !== 'justify-right') {
        li.setAttribute('data-direction', 'rtl');
      }
      li.setAttribute('style', getTextBlockAlignmentStyle(align, { rtl }));
    } else if (direction) {
      li.setAttribute('data-direction', direction);
    }

    while (div.firstChild) {
      li.appendChild(div.firstChild);
    }
    list.appendChild(li);
  }
};

const LIST_ITEM_CONFLICTING_STYLE_KEYS = new Set([
  'margin-inline-start',
  'margin-left',
  'margin-right',
  'text-indent',
  'padding-inline-start',
  'padding-left',
  'padding-right',
]);

const stripListItemConflictingStyles = (style: string): string =>
  style
    .split(';')
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      const key = part.split(':')[0]?.trim().toLowerCase();
      return key && !LIST_ITEM_CONFLICTING_STYLE_KEYS.has(key);
    })
    .join('; ');

const STRIPPED_FONT_STYLE_KEYS = new Set(['font-family', 'font']);

/** Remove pasted font faces — lesson text uses the learner/admin default stack only. */
const stripFontFamilyFromTree = (root: HTMLElement): void => {
  root.querySelectorAll('font').forEach((font) => {
    const parent = font.parentNode;
    if (!parent) return;
    while (font.firstChild) {
      parent.insertBefore(font.firstChild, font);
    }
    parent.removeChild(font);
  });

  root.querySelectorAll('[style]').forEach((el) => {
    const htmlEl = el as HTMLElement;
    const style = htmlEl.getAttribute('style');
    if (!style) return;
    const cleaned = style
      .split(';')
      .map((part) => part.trim())
      .filter((part) => {
        if (!part) return false;
        const key = part.split(':')[0]?.trim().toLowerCase();
        return key && !STRIPPED_FONT_STYLE_KEYS.has(key);
      })
      .join('; ');
    if (cleaned.trim()) {
      htmlEl.setAttribute('style', cleaned);
    } else {
      htmlEl.removeAttribute('style');
    }
  });

  root.querySelectorAll('[face]').forEach((el) => {
    el.removeAttribute('face');
  });
};

const isBoldStyle = (style: string, tag: string): boolean => {
  if (INLINE_FORMAT_TAGS.has(tag) && (tag === 'B' || tag === 'STRONG')) return true;
  const weight = parseStyleMap(style).get('font-weight')?.toLowerCase();
  return weight === 'bold' || weight === 'bolder' || (!!weight && parseInt(weight, 10) >= 600);
};

const isItalicStyle = (style: string, tag: string): boolean => {
  if (INLINE_FORMAT_TAGS.has(tag) && (tag === 'I' || tag === 'EM')) return true;
  return parseStyleMap(style).get('font-style')?.toLowerCase() === 'italic';
};

const isUnderlineStyle = (style: string, tag: string): boolean => {
  if (tag === 'U') return true;
  const deco = parseStyleMap(style).get('text-decoration')?.toLowerCase() || '';
  return deco.includes('underline');
};

const appendNodes = (target: Node, source: Node | DocumentFragment) => {
  if (source.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
    while (source.firstChild) {
      target.appendChild(source.firstChild);
    }
    return;
  }
  target.appendChild(source);
};

const wrapNodes = (doc: Document, tag: string, content: Node | DocumentFragment): HTMLElement => {
  const wrapper = doc.createElement(tag);
  appendNodes(wrapper, content);
  return wrapper;
};

const wrapColorAndHighlight = (
  doc: Document,
  content: Node | DocumentFragment,
  style: string,
  colorAttr?: string | null
): Node | DocumentFragment => {
  const map = parseStyleMap(style);
  const color = normalizeColorValue(map.get('color') || colorAttr);
  const background = normalizeColorValue(map.get('background-color') || map.get('background'));
  if (!color && !background) return content;

  const span = doc.createElement('span');
  if (color) span.style.color = color;
  if (background) span.style.backgroundColor = background;
  appendNodes(span, content);
  return span;
};

const applyInlineStyles = (
  doc: Document,
  content: DocumentFragment,
  style: string,
  tag: string,
  _face: string | null,
  colorAttr?: string | null
): Node | DocumentFragment => {
  let result: Node | DocumentFragment = content;

  if (isBoldStyle(style, tag)) {
    result = wrapNodes(doc, 'b', result);
  }
  if (isItalicStyle(style, tag)) {
    result = wrapNodes(doc, 'i', result);
  }
  if (isUnderlineStyle(style, tag)) {
    result = wrapNodes(doc, 'u', result);
  }

  return wrapColorAndHighlight(doc, result, style, colorAttr);
};

const sanitizeChildren = (el: Element, doc: Document, inheritedDir: 'rtl' | 'ltr' | null): DocumentFragment => {
  const frag = doc.createDocumentFragment();
  for (const child of Array.from(el.childNodes)) {
    const sanitized = sanitizeNode(child, doc, inheritedDir);
    if (sanitized) appendNodes(frag, sanitized);
  }
  return frag;
};

const getInheritedDirFromElement = (
  el: HTMLElement,
  inheritedDir: 'rtl' | 'ltr' | null
): 'rtl' | 'ltr' | null => {
  const dir = el.getAttribute('dir')?.toLowerCase();
  if (dir === 'rtl' || dir === 'ltr') return dir;
  const styleDir = parseStyleMap(el.getAttribute('style') || '').get('direction')?.toLowerCase();
  if (styleDir === 'rtl' || styleDir === 'ltr') return styleDir;
  const lang = el.getAttribute('lang')?.split('-')[0]?.toLowerCase();
  if (lang && RTL_LANG_CODES.has(lang)) return 'rtl';
  const cls = el.getAttribute('class')?.toLowerCase() || '';
  if (cls.includes('msortl') || /\brtl\b/.test(cls)) return 'rtl';
  if (el.getAttribute('data-direction') === 'rtl') return 'rtl';
  return inheritedDir;
};

const sanitizeNode = (
  node: Node,
  doc: Document,
  inheritedDir: 'rtl' | 'ltr' | null
): Node | DocumentFragment | null => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    return text.length > 0 ? doc.createTextNode(text) : null;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const el = node as HTMLElement;
  const tag = el.tagName;

  if (DANGEROUS_TAGS.has(tag)) return null;

  const elementDir = getInheritedDirFromElement(el, inheritedDir);

  if (tag === 'BR') {
    return doc.createElement('br');
  }

  if (tag === 'IMG') {
    return null;
  }

  if (tag === 'MARK') {
    const span = doc.createElement('span');
    const bg = normalizeColorValue(
      parseStyleMap(el.getAttribute('style') || '').get('background-color') || '#ffff00'
    );
    if (bg) span.style.backgroundColor = bg;
    appendNodes(span, sanitizeChildren(el, doc, elementDir));
    return span.childNodes.length ? span : null;
  }

  if (tag === 'A') {
    const href = el.getAttribute('href') || '';
    if (!isAllowedHref(href)) {
      return sanitizeChildren(el, doc, elementDir);
    }
    const anchor = doc.createElement('a');
    anchor.setAttribute('href', href);
    anchor.setAttribute('rel', 'noopener noreferrer');
    if (/^https?:\/\//i.test(href)) {
      anchor.setAttribute('target', '_blank');
    }
    appendNodes(anchor, sanitizeChildren(el, doc, elementDir));
    return anchor.childNodes.length ? anchor : null;
  }

  if (tag === 'UL' || tag === 'OL') {
    const list = doc.createElement(tag.toLowerCase());
    const paragraph = resolveParagraphAlign(el, elementDir, inheritedDir);
    if (paragraph) {
      const probe = doc.createElement('div');
      probe.setAttribute('data-align', paragraph.align);
      if (paragraph.rtl) probe.setAttribute('data-direction', 'rtl');
      applyListDirectionFromBlock(list, probe);
    } else if (elementDir === 'rtl' || elementDir === 'ltr') {
      list.setAttribute('dir', elementDir);
    }
    appendNodes(list, sanitizeChildren(el, doc, elementDir));
    return list.childNodes.length ? list : null;
  }

  if (tag === 'LI') {
    const li = doc.createElement('li');
    const paragraph = resolveParagraphAlign(el, elementDir, inheritedDir);
    applyParagraphAttrs(li, paragraph);
    appendNodes(li, sanitizeChildren(el, doc, elementDir));
    stripLeadingListMarkerFromBlock(li);
    return li.childNodes.length ? li : null;
  }

  if (TABLE_SECTION_TAGS.has(tag)) {
    const tableEl = doc.createElement(tag.toLowerCase()) as HTMLElement;
    if (tag === 'TABLE') {
      applyTableDirection(tableEl as HTMLTableElement, el, elementDir ?? inheritedDir);
    }
    appendNodes(tableEl, sanitizeChildren(el, doc, elementDir));
    if (tag === 'TR' && !tableEl.childNodes.length) return null;
    if ((tag === 'TBODY' || tag === 'THEAD' || tag === 'TFOOT') && !tableEl.childNodes.length) return null;
    return tableEl;
  }

  if (tag === 'TD' || tag === 'TH') {
    const cell = doc.createElement(tag.toLowerCase());
    const cellStyle = detectCellAlignmentStyle(el.getAttribute('style') || '', el.getAttribute('dir'));
    if (cellStyle) cell.setAttribute('style', cellStyle);
    appendNodes(cell, sanitizeChildren(el, doc, elementDir));
    return cell.childNodes.length ? cell : null;
  }

  if (tag === 'B' || tag === 'STRONG') {
    const b = doc.createElement('b');
    appendNodes(b, sanitizeChildren(el, doc, elementDir));
    return b.childNodes.length ? b : null;
  }

  if (tag === 'I' || tag === 'EM') {
    const i = doc.createElement('i');
    appendNodes(i, sanitizeChildren(el, doc, elementDir));
    return i.childNodes.length ? i : null;
  }

  if (tag === 'U') {
    const u = doc.createElement('u');
    appendNodes(u, sanitizeChildren(el, doc, elementDir));
    return u.childNodes.length ? u : null;
  }

  if (tag === 'SPAN' || tag === 'FONT') {
    const children = sanitizeChildren(el, doc, elementDir);
    if (!children.childNodes.length) return null;
    return applyInlineStyles(
      doc,
      children,
      el.getAttribute('style') || '',
      tag,
      el.getAttribute('face'),
      el.getAttribute('color')
    );
  }

  if (BLOCK_TAGS.has(tag)) {
    const children = sanitizeChildren(el, doc, elementDir);
    const paragraph = resolveParagraphAlign(el, elementDir, inheritedDir);

    const childNodes = Array.from((children as DocumentFragment).childNodes);
    const allDivChildren =
      childNodes.length > 0 &&
      childNodes.every(
        (node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'DIV'
      );

    if (allDivChildren) {
      if (paragraph) {
        childNodes.forEach((node) => {
          const inner = node as HTMLDivElement;
          if (!inner.getAttribute('data-align')) {
            applyParagraphAttrs(inner, paragraph);
          }
        });
      }
      return children;
    }

    const div = doc.createElement('div');
    const stored = tag === 'DIV' ? readStoredParagraphAlign(el) : null;
    const resolvedParagraph = stored ?? paragraph;
    applyParagraphAttrs(div, resolvedParagraph);
    appendNodes(div, children);

    if (isBlankLineElement(el) && !hasMeaningfulBlockContent(div)) {
      return ensureBlankLineDiv(doc, div);
    }
    if (isListMarkerOnlyText(div.textContent) && !div.querySelector('table, ul, ol, img, br')) {
      div.textContent = '';
      return ensureBlankLineDiv(doc, div);
    }

    applyWordBlockLayout(div, el, resolvedParagraph, elementDir);

    if (isListMarkerOnlyText(div.textContent) && !div.querySelector('table, ul, ol, img, br')) {
      div.textContent = '';
      div.removeAttribute('data-list-item');
      div.removeAttribute('data-list-level');
      return ensureBlankLineDiv(doc, div);
    }
    if (!hasMeaningfulBlockContent(div)) {
      return null;
    }
    return div;
  }

  if (tag === 'HTML' || tag === 'BODY') {
    return sanitizeChildren(el, doc, elementDir);
  }

  const unwrapped = sanitizeChildren(el, doc, elementDir);
  return unwrapped.childNodes.length ? unwrapped : null;
};

/**
 * Sanitize HTML for lesson text blocks (paste and save).
 * Returns empty string when nothing usable remains.
 */
export const sanitizeLessonHtml = (html: string): string => {
  if (!html?.trim()) return '';

  const documentDir = extractDocumentDirection(html);
  const cleaned = preprocessClipboardHtml(html);
  const doc = document.implementation.createHTMLDocument('');
  const root = doc.createElement('div');
  root.innerHTML = cleaned;

  const output = doc.createElement('div');
  for (const child of Array.from(root.childNodes)) {
    const sanitized = sanitizeNode(child, doc, documentDir);
    if (sanitized) appendNodes(output, sanitized);
  }

  groupConsecutiveListDivs(output, doc);

  output.querySelectorAll('li').forEach((li) => {
    stripLeadingListMarkerFromBlock(li);
    if (!visibleText(li.textContent)) {
      li.remove();
      return;
    }
    const style = li.getAttribute('style');
    if (style) {
      const cleaned = stripListItemConflictingStyles(style);
      if (cleaned.trim()) li.setAttribute('style', cleaned);
      else li.removeAttribute('style');
    }
  });

  output.querySelectorAll('div').forEach((div) => {
    if (div.querySelector('table, ul, ol, img')) return;
    if (isBlankLineElement(div) || isListMarkerOnlyText(div.textContent)) {
      ensureBlankLineDiv(doc, div);
      return;
    }
    if (!hasMeaningfulBlockContent(div)) {
      div.remove();
    }
  });

  output.querySelectorAll('ul, ol').forEach((list) => {
    if (!list.getAttribute('dir')) {
      const firstLi = list.querySelector('li');
      if (firstLi) {
        applyListDirectionFromBlock(list as HTMLElement, firstLi as HTMLElement);
      }
    }
    if (!list.querySelector('li')) {
      list.remove();
    }
  });

  propagateRtlParagraphContext(output, documentDir);

  collapseConsecutiveBlankDivs(output, doc);

  stripFontFamilyFromTree(output);

  const result = output.innerHTML.trim();
  if (result) return result;

  const plain = root.textContent?.trim();
  return plain ? plain.replace(/\r\n/g, '\n') : '';
};

/** @deprecated Use sanitizeLessonHtml — kept for existing imports. */
export const sanitizeWordPasteHtml = sanitizeLessonHtml;
